import { useTranslation } from 'next-i18next'
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { DepositAnswer } from '@fe/common/api/models/DepositAnswer'
import { LabeldList } from '@fe/common/components/LabeldList'
import { LoadingDots } from '@fe/common/components/LoadingDots'
import { ErrorText } from '@fe/common/components/typography/ErrorText'
import { ReceiveData } from '../..'
import { requisitesErrorText } from '../requisitesErrorText'
import {
  getBankDetailsConfig,
  TypeWithBankDetails,
} from './getBankDetailsConfig'
import { DepositsApi } from 'src/api/DepositsApi'
import { countriesSelector } from 'src/state/selectors/globalDataSelectors'

export const BankDetails: React.FC = () => {
  const { t } = useTranslation('transactions')
  const countries = useSelector(countriesSelector)
  const [isLoading, setIsLoading] = useState(false)
  const [detailsData, setDetailsData] = useState<DepositAnswer>()
  const [error, setError] = useState<string>()
  // BankDetails stays mounted while the request type changes, so an answer
  // of the previous type can come late. Only the latest fetch may set state.
  const lastRequest = useRef(0)

  const { getValues, watch } = useFormContext<ReceiveData>()

  const requestType = watch('request_type') as TypeWithBankDetails

  const fetchDetails = useCallback(async (requestType: TypeWithBankDetails) => {
    const request = ++lastRequest.current
    const isLatest = () => request === lastRequest.current

    setDetailsData(undefined)
    setError(undefined)

    try {
      setIsLoading(true)

      const { settings_id } = getValues()

      const { data } = await DepositsApi.getBankDetails(
        requestType,
        settings_id
      )

      if (isLatest()) {
        setDetailsData(data)
      }
    } catch (error) {
      if (isLatest()) {
        setError(requisitesErrorText(error, t))
      }
    } finally {
      if (isLatest()) {
        setIsLoading(false)
      }
    }
  }, [])

  useLayoutEffect(() => {
    fetchDetails(requestType)
  }, [requestType])

  const summaryFields = useMemo(
    () =>
      getBankDetailsConfig(detailsData, countries)[requestType].map(
        ({ label, value }) =>
          (value || isLoading) && (
            <Fragment key={label as string}>
              <span>{t(label as string)}</span>
              {isLoading ? <LoadingDots /> : <span>{value}</span>}
            </Fragment>
          )
      ),
    [isLoading, detailsData, requestType]
  )

  if (error) {
    return <ErrorText>{error}</ErrorText>
  }

  return <LabeldList>{summaryFields}</LabeldList>
}
